import React from 'react';

import { Typography } from 'antd';

import SectionStatusContent, { type SectionStatus } from '../SectionStatus';
import MetricCard, { MetricTooltip, type MetricCardItem, type MetricTooltipProps } from './MetricCard';
import styles from './styles.module.less';

const { Text } = Typography;

export interface MetricCardGroupProps {
  label: string;
  items: MetricCardItem[];
  /** Number of columns at wide desktop sizes; smaller screens still reflow. */
  columns?: 6 | 7 | 8;
  /** Load state of the group's cards; the label stays visible in every state. */
  status?: SectionStatus;
}

/** Labeled row of KPI cards. */
export const MetricCardGroup: React.FC<MetricCardGroupProps> = ({ label, items, columns = 6, status }) => {
  const columnClassName = columns === 7
    ? styles.sevenColumns
    : columns === 8
      ? styles.eightColumns
      : '';
  const gridClassName = columnClassName ? `${styles.grid} ${columnClassName}` : styles.grid;

  return (
    <div className={styles.group} aria-busy={status?.kind === 'loading'}>
      <Text className={styles.groupLabel}>{label}</Text>
      <SectionStatusContent status={status}>
        <div className={gridClassName}>
          {items.map(({ key, ...item }) => (
            <MetricCard key={key} {...item} />
          ))}
        </div>
      </SectionStatusContent>
    </div>
  );
};

export interface MetricsPanelProps {
  ariaLabel: string;
  groups: MetricCardGroupProps[];
}

/** Bordered panel stacking several KPI groups. */
const MetricsPanel: React.FC<MetricsPanelProps> = ({ ariaLabel, groups }) => (
  <section className={styles.panel} aria-label={ariaLabel}>
    {groups.map(group => (
      <MetricCardGroup key={group.label} {...group} />
    ))}
  </section>
);

export type { MetricCardItem };
export { MetricCard, MetricTooltip };
export type { MetricTooltipProps };
export default MetricsPanel;
